import { CheckIcon, TrashIcon } from "@heroicons/react/24/outline";
import { SelectItem } from "@heroui/react";
import AppSelect from "../../common/appSelect";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MEASURE_RANGES, MeasureKey, UnitKey, UpdateProfileUIProps } from "../../../types/updateProfileProps";
import {
    AddPill,
    Card,
    FieldError,
    FieldLabel,
    FormActions,
    Option,
    SectionTitle,
    SelectField,
    TextAreaField,
    TextField,
} from "./formPrimitives";

const ACHIEVEMENT_MAX = 5000;

/** "MIXED_MARTIAL_ARTS" -> "Mixed Martial Arts" */
const formatLabel = (value: string) => {
    const withSpaces = value.replace(/_/g, " ").toLowerCase();
    return withSpaces.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

type MeasureRowProps = Pick<UpdateProfileUIProps, "form" | "setField" | "errors"> & {
    field: MeasureKey;
    unitField: UnitKey;
    label: string;
    units: Option[];
};

/** Figma row: [value input ........] [Unit ▾] */
const MeasureRow: React.FC<MeasureRowProps> = ({ form, setField, errors, field, unitField, label, units }) => {
    const { t } = useTranslation("updateProfile");
    const unit = form[unitField];
    const range = MEASURE_RANGES[field][unit];
    const placeholder = range ? `e.g., ${range[0]} - ${range[1]}` : "";

    return (
        <div className="flex w-full items-start gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <FieldLabel>{label}</FieldLabel>
                <TextField
                    name={field}
                    inputMode="numeric"
                    maxLength={3}
                    value={form[field]}
                    placeholder={placeholder}
                    onValueChange={(value) => setField(field, value.replace(/\D/g, ""))}
                    error={errors[field]}
                />
            </div>
            <div className="w-[100px] shrink-0">
                <SelectField
                    name={unitField}
                    label={t("page.unit")}
                    value={unit}
                    placeholder={t("page.unit")}
                    options={units}
                    onValueChange={(value) => setField(unitField, value)}
                />
            </div>
        </div>
    );
};

type MultiSelectProps = {
    label: string;
    placeholder: string;
    values: string[];
    options: string[];
    error?: string;
    onValuesChange: (values: string[]) => void;
};

/** Figma multi select (disciplines / weight classes) */
const MultiSelect: React.FC<MultiSelectProps> = ({ label, placeholder, values, options, error, onValuesChange }) => {
    // keep already-saved values selectable even if the option list is still loading
    const allOptions = useMemo(() => Array.from(new Set([...options, ...values])), [options, values]);

    const handleSelectionChange = (keys: "all" | Set<React.Key>) => {
        if (keys === "all") {
            onValuesChange(allOptions);
            return;
        }
        onValuesChange(Array.from(keys).map(String));
    };

    return (
        <div className="flex w-full flex-col gap-1">
            <FieldLabel>{label}</FieldLabel>
            <AppSelect
                aria-label={label}
                selectionMode="multiple"
                placeholder={placeholder}
                selectedKeys={new Set(values)}
                onSelectionChange={handleSelectionChange}
                isInvalid={!!error}
                classNames={{
                    base: "w-full",
                    trigger:
                        "h-9 min-h-9 rounded-xl bg-[#EBEBEC] px-3 shadow-none data-[hover=true]:bg-[#EBEBEC] data-[open=true]:bg-white",
                    value: "text-sm text-gray-900",
                    selectorIcon: "text-gray-900",
                    popoverContent: "rounded-xl",
                }}
            >
                {allOptions.map((option) => (
                    <SelectItem key={option}>{formatLabel(option)}</SelectItem>
                ))}
            </AppSelect>
            <FieldError message={error} />
        </div>
    );
};

/** Figma checkbox: 16px grey square → blue when checked */
const FigmaCheckbox: React.FC<{
    checked: boolean;
    label: string;
    onChange: (checked: boolean) => void;
}> = ({ checked, label, onChange }) => (
    <label className="relative inline-flex w-fit cursor-pointer items-center gap-3">
        <input
            type="checkbox"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            className="peer sr-only"
        />
        <span
            className={`flex h-4 w-4 items-center justify-center rounded-md transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-blue-300 ${
                checked ? "bg-[#0485F7]" : "bg-[#EBEBEC]"
            }`}
        >
            {checked && <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} />}
        </span>
        <span className="text-sm font-medium leading-5 text-gray-900">{label}</span>
    </label>
);

// ── Achievements (inside "Fighter profile basic details" in Figma) ──────────
const AchievementsBlock: React.FC<UpdateProfileUIProps> = ({ achievements, commitAchievements, isPersisting }) => {
    const { t } = useTranslation("updateProfile");
    const [isAdding, setIsAdding] = useState(false);
    const [text, setText] = useState("");
    const [error, setError] = useState("");

    const closeForm = () => {
        setIsAdding(false);
        setText("");
        setError("");
    };

    const addAchievement = async () => {
        const value = text.trim();
        if (!value) {
            setError(t("page.errors.required"));
            return;
        }
        if (value.length > ACHIEVEMENT_MAX) {
            setError(t("page.errors.maxLength", { max: ACHIEVEMENT_MAX }));
            return;
        }
        const isSaved = await commitAchievements([...achievements, value]);
        if (isSaved) closeForm();
    };

    const removeAchievement = (index: number) => {
        commitAchievements(achievements.filter((_, itemIndex) => itemIndex !== index));
    };

    return (
        <div className="flex flex-col gap-4">
            {!isAdding && (
                <AddPill onClick={() => setIsAdding(true)} disabled={isPersisting}>
                    {t("page.addAchievement")}
                </AddPill>
            )}

            {achievements.map((achievement, index) => (
                <Card key={`${index}-${achievement}`} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm leading-5 text-gray-500">{t("page.achievementN", { n: index + 1 })}</p>
                        <p className="break-words text-base font-bold leading-6 text-gray-900">{achievement}</p>
                    </div>
                    <button
                        type="button"
                        aria-label={t("page.remove")}
                        disabled={isPersisting}
                        onClick={() => removeAchievement(index)}
                        className="mt-1 shrink-0 text-gray-900 transition-colors hover:text-red-600 disabled:opacity-50"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </Card>
            ))}

            {isAdding && (
                <Card className="flex flex-col gap-3">
                    <TextField
                        name="achievement"
                        label={t("page.achievementN", { n: achievements.length + 1 })}
                        value={text}
                        maxLength={ACHIEVEMENT_MAX}
                        placeholder={t("page.enterAchievement")}
                        onValueChange={(value) => {
                            setText(value);
                            setError("");
                        }}
                        hint={t("page.characters", { count: text.length, max: ACHIEVEMENT_MAX })}
                        error={error}
                    />
                    <FormActions
                        cancelLabel={t("page.cancel")}
                        confirmLabel={t("page.addAchievementBtn")}
                        busy={isPersisting}
                        onCancel={closeForm}
                        onConfirm={addAchievement}
                    />
                </Card>
            )}
        </div>
    );
};

/** Figma "Fighter profile basic details" */
export const FighterBasicSection: React.FC<UpdateProfileUIProps> = (props) => {
    const { t } = useTranslation("updateProfile");
    const { form, setField, errors, disciplineOptions, weightClassOptions } = props;

    const lengthUnits: Option[] = [
        { value: "cm", label: t("page.units.cm") },
        { value: "ft", label: t("page.units.ft") },
        { value: "inch", label: t("page.units.inch") },
    ];
    const weightUnits: Option[] = [
        { value: "kgs", label: t("page.units.kgs") },
        { value: "lbs", label: t("page.units.lbs") },
    ];
    const stanceOptions: Option[] = [
        { value: "Orthodox", label: t("page.stances.Orthodox") },
        { value: "Southpaw", label: t("page.stances.Southpaw") },
        { value: "Switch", label: t("page.stances.Switch") },
    ];

    return (
        <section id="section-fighter-basic" className="flex flex-col gap-4">
            <SectionTitle>{t("page.fighterBasic")}</SectionTitle>

            <MultiSelect
                label={t("page.disciplines")}
                placeholder={t("page.disciplinesPlaceholder")}
                values={form.trainingDisciplines}
                options={disciplineOptions}
                error={errors.trainingDisciplines}
                onValuesChange={(values) => setField("trainingDisciplines", values)}
            />
            <MultiSelect
                label={t("page.weightClasses")}
                placeholder={t("page.weightClassesPlaceholder")}
                values={form.weightClasses}
                options={weightClassOptions}
                error={errors.weightClasses}
                onValuesChange={(values) => setField("weightClasses", values)}
            />

            <MeasureRow
                form={form}
                setField={setField}
                errors={errors}
                field="height"
                unitField="heightUnit"
                label={t("page.height")}
                units={lengthUnits}
            />
            <MeasureRow
                form={form}
                setField={setField}
                errors={errors}
                field="weight"
                unitField="weightUnit"
                label={t("page.weight")}
                units={weightUnits}
            />
            <MeasureRow
                form={form}
                setField={setField}
                errors={errors}
                field="reach"
                unitField="reachUnit"
                label={t("page.reach")}
                units={lengthUnits}
            />

            <SelectField
                name="stance"
                label={t("page.stance")}
                value={form.stance}
                placeholder={t("page.stancePlaceholder")}
                options={stanceOptions}
                onValueChange={(value) => setField("stance", value)}
                error={errors.stance}
            />

            <FigmaCheckbox
                checked={form.titleHolder}
                label={t("page.titleHolder")}
                onChange={(checked) => setField("titleHolder", checked)}
            />


            <AchievementsBlock {...props} />
        </section>
    );
};

/** Figma "Fighter profile additional details" */
export const FighterAdditionalSection: React.FC<UpdateProfileUIProps> = ({
    form,
    setField,
    errors,
}) => {
    const { t } = useTranslation("updateProfile");

    return (
        <section id="section-fighter-additional" className="flex flex-col gap-4">
            <SectionTitle>{t("page.fighterAdditional")}</SectionTitle>

            <TextField
                name="team"
                label={t("page.team")}
                maxLength={30}
                value={form.team}
                placeholder={t("page.enterTeam")}
                onValueChange={(value) => setField("team", value)}
                error={errors.team}
            />
            <TextField
                name="gym"
                label={t("page.gym")}
                maxLength={30}
                value={form.gym}
                placeholder={t("page.enterGym")}
                onValueChange={(value) => setField("gym", value)}
                error={errors.gym}
            />
            <TextAreaField
                name="fightStyleDescription"
                label={t("page.fightStyle")}
                required
                maxLength={5000}
                value={form.fightStyleDescription}
                placeholder={t("page.writeHere")}
                onValueChange={(value) => setField("fightStyleDescription", value)}
                counterText={t("page.characters", {
                    count: form.fightStyleDescription.length,
                    max: 5000,
                })}
                error={errors.fightStyleDescription}
            />
        </section>
    );
};
